import type {
  AdCreativeApiClient,
  CreativeDecisionRefreshHandoffPackage,
} from '../../services/AdCreativeApiClient';

const MAX_VALUE_LENGTH = 512;
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;
const RFC3339_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const REFRESH_HANDOFF_QUERY_KEYS = new Set([
  'mode',
  'sourceHandoffVersion',
  'sourceDraftCatalog',
  'sourceProviderMutationAllowed',
  'sourceReviewRequired',
  'sourceFrom',
  'sourceTo',
  'sourceDeployment',
  'sourceProjection',
  'sourceProjectionVersion',
  'sourceScope',
  'sourceProvider',
  'sourceProviderAccount',
  'sourceProviderResource',
  'sourceCapabilitySnapshot',
  'sourceDecision',
  'sourcePrimaryAction',
  'sourceAsOf',
  'sourceRevision',
  'sourceArtifactSnapshot',
  'sourceDecisionMetric',
  'sourceEvidenceRef',
]);

export type CreativeDecisionRefreshVariation =
  | 'test_hooks_and_visuals'
  | 'test_reels_adaptation'
  | 'test_alternative_angles';

export type CreativeDecisionRefreshStoppingPolicy =
  | 'fixed_horizon_no_peeking'
  | 'registered_sequential';

export interface CreativeDecisionRefreshSourceHandoff {
  schemaVersion: 1;
  sourceKind: 'creative_decision';
  sourceFrom: string;
  sourceTo: string;
  deploymentKey: string;
  projectionId: string;
  projectionVersion: string;
  scopeRef: string;
  provider: CreativeDecisionRefreshHandoffPackage['provider'];
  providerAccountId: string;
  providerResourceName: string;
  capabilitySnapshotId: string;
  sourceRevisionRefs: string[];
  artifactSnapshotKeys: string[];
  decision: 'refresh_due';
  primaryAction: 'create_refresh_experiment';
  decisionMetricKeys: string[];
  evidenceRefs: string[];
  sourceAsOf: string;
}

export type CreativeDecisionRefreshParseResult =
  | { status: 'not_requested' }
  | { status: 'invalid'; code: 'invalid_refresh_handoff'; message: string }
  | { status: 'valid'; handoff: CreativeDecisionRefreshSourceHandoff };

export type CreativeDecisionRefreshAvailability =
  | { status: 'available'; networkId: 'meta' }
  | {
    status: 'unavailable';
    code: 'authoring_catalog_unavailable';
    provider: 'google_ads' | 'tiktok_ads';
    message: string;
  };

export interface BuildCreativeDecisionRefreshDraftOptions {
  projectId: string;
  handoff: CreativeDecisionRefreshSourceHandoff;
  variation: CreativeDecisionRefreshVariation;
  minimumDetectableEffectPercent: number;
  minimumDurationDays: number;
  stoppingPolicy: CreativeDecisionRefreshStoppingPolicy;
}

export const CREATIVE_DECISION_REFRESH_VARIATIONS: ReadonlyArray<{
  id: CreativeDecisionRefreshVariation;
  label: string;
  description: string;
  factorsToVary: readonly string[];
  templateIds: readonly string[];
}> = [
  {
    id: 'test_hooks_and_visuals',
    label: 'Hooks and visuals',
    description: 'Keep the registered deployment context fixed; vary hook and visual execution.',
    factorsToVary: ['hook', 'visual_execution'],
    templateIds: [
      'meta_feed_single_image_proof_hook',
      'meta_story_static_offer',
      'meta_reel_creator_native_hook',
    ],
  },
  {
    id: 'test_reels_adaptation',
    label: 'Reels adaptation',
    description: 'Test a native vertical format and motion execution without claiming it will win.',
    factorsToVary: ['format', 'motion_execution'],
    templateIds: ['meta_reel_creator_native_hook'],
  },
  {
    id: 'test_alternative_angles',
    label: 'Alternative angles',
    description: 'Keep the registered offer context fixed; vary message angle, proof framing, and hook.',
    factorsToVary: ['hook', 'message_angle', 'proof_framing'],
    templateIds: ['meta_feed_single_image_proof_hook', 'meta_story_static_offer'],
  },
] as const;

const REFRESH_GUARDRAILS = [
  'complaint_rate',
  'dispute_rate',
  'effective_cancel_rate',
  'payment_recovery_rate',
  'refund_rate',
] as const;

/**
 * Strict URL boundary for the read-only Creative Decisions handoff.
 * Invalid values reject the whole package; they are never silently dropped.
 */
export function parseCreativeDecisionRefreshHandoff(
  params: URLSearchParams,
): CreativeDecisionRefreshParseResult {
  if (params.get('mode') !== 'refresh-experiment-draft') {
    return { status: 'not_requested' };
  }

  try {
    assertAllowedQueryKeys(params);
    if (requiredSingle(params, 'mode') !== 'refresh-experiment-draft') {
      fail('mode must be refresh-experiment-draft');
    }
    const version = requiredSingle(params, 'sourceHandoffVersion');
    if (version !== '1') fail('sourceHandoffVersion must be 1');
    if (requiredSingle(params, 'sourceDraftCatalog') !== 'segmently-provider-neutral-v1') {
      fail('sourceDraftCatalog is not supported');
    }
    if (requiredSingle(params, 'sourceProviderMutationAllowed') !== 'false') {
      fail('sourceProviderMutationAllowed must be false');
    }
    if (requiredSingle(params, 'sourceReviewRequired') !== 'true') {
      fail('sourceReviewRequired must be true');
    }

    const sourceFrom = requiredDate(params, 'sourceFrom');
    const sourceTo = requiredDate(params, 'sourceTo');
    if (sourceFrom > sourceTo) fail('sourceFrom must be on or before sourceTo');

    const provider = requiredSingle(params, 'sourceProvider');
    if (!isSupportedProvider(provider)) fail('sourceProvider is not supported');

    const decision = requiredSingle(params, 'sourceDecision');
    if (decision !== 'refresh_due') fail('sourceDecision must be refresh_due');

    const primaryAction = requiredSingle(params, 'sourcePrimaryAction');
    if (primaryAction !== 'create_refresh_experiment') {
      fail('sourcePrimaryAction must be create_refresh_experiment');
    }

    const sourceAsOf = requiredSingle(params, 'sourceAsOf');
    if (!RFC3339_TIMESTAMP.test(sourceAsOf) || Number.isNaN(Date.parse(sourceAsOf))) {
      fail('sourceAsOf must be an RFC 3339 timestamp');
    }

    const sourceRevisionRefs = strictList(params, 'sourceRevision', 100);
    if (sourceRevisionRefs.length === 0) fail('sourceRevision requires at least one value');

    return {
      status: 'valid',
      handoff: {
        schemaVersion: 1,
        sourceKind: 'creative_decision',
        sourceFrom,
        sourceTo,
        deploymentKey: requiredSingle(params, 'sourceDeployment'),
        projectionId: requiredSingle(params, 'sourceProjection'),
        projectionVersion: requiredSingle(params, 'sourceProjectionVersion'),
        scopeRef: requiredSingle(params, 'sourceScope'),
        provider,
        providerAccountId: requiredSingle(params, 'sourceProviderAccount'),
        providerResourceName: requiredSingle(params, 'sourceProviderResource'),
        capabilitySnapshotId: requiredSingle(params, 'sourceCapabilitySnapshot'),
        sourceRevisionRefs,
        artifactSnapshotKeys: strictList(params, 'sourceArtifactSnapshot', 25),
        decision: 'refresh_due',
        primaryAction: 'create_refresh_experiment',
        decisionMetricKeys: strictList(params, 'sourceDecisionMetric', 25),
        evidenceRefs: strictList(params, 'sourceEvidenceRef', 100),
        sourceAsOf: new Date(sourceAsOf).toISOString(),
      },
    };
  } catch (error) {
    return {
      status: 'invalid',
      code: 'invalid_refresh_handoff',
      message: error instanceof Error ? error.message : 'The refresh handoff is invalid.',
    };
  }
}

function assertAllowedQueryKeys(params: URLSearchParams): void {
  for (const key of params.keys()) {
    if (!REFRESH_HANDOFF_QUERY_KEYS.has(key)) {
      // Do not reflect an attacker-controlled/private key name into the UI.
      fail('The refresh handoff contains unsupported query parameters');
    }
  }
}

function requiredDate(params: URLSearchParams, key: string): string {
  const value = requiredSingle(params, key);
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (!DATE_ONLY.test(value)
    || Number.isNaN(parsed.getTime())
    || parsed.toISOString().slice(0, 10) !== value) {
    fail(`${key} must be a YYYY-MM-DD date`);
  }
  return value;
}

export function resolveCreativeDecisionRefreshAvailability(
  handoff: CreativeDecisionRefreshSourceHandoff,
): CreativeDecisionRefreshAvailability {
  if (handoff.provider === 'meta') return { status: 'available', networkId: 'meta' };
  const providerLabel = handoff.provider === 'google_ads' ? 'Google Ads' : 'TikTok Ads';
  return {
    status: 'unavailable',
    code: 'authoring_catalog_unavailable',
    provider: handoff.provider,
    message: `${providerLabel} source analysis is supported by the handoff contract, but its Ad Creative authoring catalog is not available yet. The source deployment remains read-only.`,
  };
}

/**
 * Builds the only mutating request in this flow. It creates Segmently drafts
 * through briefs_first; it contains no provider-write or auto-launch command.
 */
export function buildCreativeDecisionRefreshDraftRequest(
  options: BuildCreativeDecisionRefreshDraftOptions,
): Parameters<AdCreativeApiClient['generateBatch']>[0] {
  const { handoff } = options;
  const availability = resolveCreativeDecisionRefreshAvailability(handoff);
  if (availability.status !== 'available') throw new Error(availability.message);
  if (!options.projectId.trim()) throw new Error('projectId is required');
  if (!Number.isFinite(options.minimumDetectableEffectPercent)
    || options.minimumDetectableEffectPercent <= 0
    || options.minimumDetectableEffectPercent > 100) {
    throw new Error('Minimum detectable effect must be greater than 0 and at most 100 percent.');
  }
  if (!Number.isInteger(options.minimumDurationDays)
    || options.minimumDurationDays < 1
    || options.minimumDurationDays > 90) {
    throw new Error('Minimum duration must be a whole number from 1 to 90 days.');
  }
  if (!['fixed_horizon_no_peeking', 'registered_sequential'].includes(options.stoppingPolicy)) {
    throw new Error('A supported stopping policy is required.');
  }

  const variation = CREATIVE_DECISION_REFRESH_VARIATIONS.find((item) => item.id === options.variation);
  if (!variation) throw new Error('A supported experiment variation is required.');

  const creativeDecisionHandoff: CreativeDecisionRefreshHandoffPackage = {
    ...handoff,
    sourceRevisionRefs: sortedUnique(handoff.sourceRevisionRefs),
    artifactSnapshotKeys: sortedUnique(handoff.artifactSnapshotKeys),
    decisionMetricKeys: sortedUnique(handoff.decisionMetricKeys),
    evidenceRefs: sortedUnique(handoff.evidenceRefs),
    variation: variation.id,
    fixedFactors: sortedUnique([
      `deployment:${handoff.deploymentKey}`,
      `measurement_scope:${handoff.scopeRef}`,
      `provider_account:${handoff.provider}:${handoff.providerAccountId}`,
      `source_projection:${handoff.projectionId}@${handoff.projectionVersion}`,
    ]),
    factorsToVary: sortedUnique(variation.factorsToVary),
    targetEstimand: 'intention_to_treat_contribution_per_assigned_user',
    primaryMetric: 'profit_per_assigned_user',
    surrogateStatus: 'diagnostic_only',
    guardrails: [...REFRESH_GUARDRAILS],
    minimumDetectableEffectPercent: options.minimumDetectableEffectPercent,
    minimumDurationDays: options.minimumDurationDays,
    stoppingPolicy: options.stoppingPolicy,
  };

  return {
    projectId: options.projectId,
    networkId: 'meta',
    title: `Refresh experiment · ${handoff.providerResourceName}`.slice(0, 160),
    promptText: `Review-first refresh experiment for ${handoff.providerResourceName}`.slice(0, 512),
    templateIds: [...variation.templateIds],
    angleCount: 3,
    mode: 'briefs_first',
    creativeDecisionHandoff,
    userInstruction: [
      'Create separate Segmently creative brief drafts for human review.',
      'The connected-account source is immutable: do not mutate, convert, pause, relaunch, or auto-launch the provider ad.',
      `Variation to test: ${variation.label} (${variation.factorsToVary.join(', ')}).`,
      `Target estimand: ${creativeDecisionHandoff.targetEstimand}.`,
      `Primary metric: ${creativeDecisionHandoff.primaryMetric}.`,
      `Minimum detectable effect selected by the operator: ${options.minimumDetectableEffectPercent}%.`,
      `Minimum duration selected by the operator: ${options.minimumDurationDays} days.`,
      `Stopping policy selected by the operator: ${options.stoppingPolicy}.`,
      'Early funnel signals remain diagnostic-only and cannot declare an economic winner.',
      'Do not infer causal performance, copy mutable delivery metrics, or generate a live ad.',
    ].join('\n'),
  };
}

function requiredSingle(params: URLSearchParams, key: string): string {
  const values = params.getAll(key);
  if (values.length !== 1) fail(`${key} must appear exactly once`);
  const value = values[0]?.trim() ?? '';
  if (!isSafeValue(value)) fail(`${key} is missing or invalid`);
  return value;
}

function strictList(params: URLSearchParams, key: string, maxUnique: number): string[] {
  const values = params.getAll(key);
  if (values.length > maxUnique * 4) fail(`${key} has too many values`);
  if (values.some((value) => !isSafeValue(value.trim()))) fail(`${key} contains an invalid value`);
  const normalized = sortedUnique(values.map((value) => value.trim()));
  if (normalized.length > maxUnique) fail(`${key} has too many unique values`);
  return normalized;
}

function isSafeValue(value: string): boolean {
  return Boolean(value)
    && value.length <= MAX_VALUE_LENGTH
    && !CONTROL_CHARACTERS.test(value);
}

function isSupportedProvider(
  value: string,
): value is CreativeDecisionRefreshHandoffPackage['provider'] {
  return value === 'meta' || value === 'google_ads' || value === 'tiktok_ads';
}

function sortedUnique(values: readonly string[]): string[] {
  return [...new Set(values)].sort(compareCodeUnits);
}

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function fail(message: string): never {
  throw new Error(message);
}
